import { watch, nextTick, onBeforeUnmount } from 'vue'
import type { Ref } from 'vue'

/**
 * Trap focus inside an element while it's open.
 *
 *   const dialogRef = ref(null)
 *   useFocusTrap(dialogRef, () => state.open)
 *
 *   <div ref="dialogRef" v-show="state.open" role="dialog">…</div>
 *
 * When `isOpen` becomes true the first focusable child receives
 * focus, Tab/Shift-Tab cycles within the element, and Escape is
 * left for the caller to handle. When `isOpen` becomes false the
 * previously focused element regains focus.
 */
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable]:not([contenteditable="false"])',
].join(',')

export function useFocusTrap(
  rootRef: Ref<HTMLElement | null | undefined>,
  isOpenFn: () => boolean
) {
  let previouslyFocused: HTMLElement | null = null

  function getFocusable(): HTMLElement[] {
    const root = rootRef.value
    if (!root) return []
    return Array.from(root.querySelectorAll(FOCUSABLE) as NodeListOf<HTMLElement>).filter(
      (el) => el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement
    )
  }

  function onKey(e: KeyboardEvent) {
    if (e.key !== 'Tab') return
    const items = getFocusable()
    if (!items.length) {
      e.preventDefault()
      rootRef.value?.focus()
      return
    }
    const first = items[0]!
    const last = items[items.length - 1]!
    const active = document.activeElement
    if (e.shiftKey) {
      if (active === first || !rootRef.value?.contains(active)) {
        e.preventDefault()
        last.focus()
      }
    } else {
      if (active === last || !rootRef.value?.contains(active)) {
        e.preventDefault()
        first.focus()
      }
    }
  }

  watch(
    isOpenFn,
    async (open) => {
      if (open) {
        previouslyFocused = document.activeElement as HTMLElement | null
        await nextTick()
        document.addEventListener('keydown', onKey, true)
        const items = getFocusable()
        ;(items[0] || rootRef.value)?.focus()
      } else {
        document.removeEventListener('keydown', onKey, true)
        if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
          previouslyFocused.focus()
        }
        previouslyFocused = null
      }
    },
    { immediate: true }
  )

  onBeforeUnmount(() => {
    document.removeEventListener('keydown', onKey, true)
  })
}
